import { RouterRootState } from "connected-react-router";
import { FC, ReactNode, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import ActivityFrame from "../../../components/activity-frame/activity-frame";
import Breadcrumb from "../../../components/breadcrumb/breadcrumb";
import Tabs, { getTabsListFromLabels } from "../../../components/tabs/tabs";
import ExpiryReasonTab from "../../../containers/expiry-reasons-tab";
import { useLocale } from "../../../contexts/localeContext";
import Restricted from "../../../contexts/PermissionProvider/RestrictedWrapper";
import { TranslationKeys } from "../../../contexts/TranslationKeys";
import plusIcon from "../../../images/Plus.svg";
import { Category } from "../../../models/Category";
import { getExpiryReasons, saveExpiryReason } from "../../../services/expiryReasonService";
import { getErrorMessage, showError } from "../../../services/utilities/ServiceError";
import { toast } from "react-toastify";
import ExpiryReasonModal from "@/components/expiry-reasons/ExpiryReasonModal";
import { ExpiryReason as ExpiryReasonModel} from "@/models/ExpiryReason";
import { Filter } from "@/models/Filter";
import { getListOfSiteIdsFromFilter } from "@/utilities/tabUtils";
import { useTabCount } from "@/hooks/useCategoryTab";
import { useDialog } from "@/components/dialog/DialogContext";

const getRouteNameFromPath = (path: string) => {
    const splitPath = path.split('/');
    return splitPath[splitPath.length - 1];
}

const initialValues: ExpiryReasonModel = {
    id: undefined as any,
    name: '',
    nameOriginal: '',
    duration: '',
    durationWarning: '',
    durationValue: 0,
    durationUnit: '',
    durationWarnValue: 0,
    eventExpiry: '',
    eventWarning: '',
}

const ExpiryReasons: FC = () => {
    const [selected, setSelected] = useState(0);
    const [busy, setBusy] = useState(false);
    const history = useHistory();
    const [categories, setCategories] = useState<Category[]>([]);
    const [tabContent, setTabContent] = useState<ReactNode[]>([]);
    const routeName = useSelector((state: RouterRootState) => getRouteNameFromPath(state.router.location.pathname));
    const { translate } = useLocale();
    const [showModal, setShowModal] = useState(false);
    const dialog = useDialog();
    const [currentItem, setCurrentItem] = useState<ExpiryReasonModel>(initialValues);
    const { updateTabCount } = useTabCount({
        tableName: "ExpiryReason",
        categories,
        setCategories,
    });

    useEffect(() => {
        buildCategories();
    }, []);

    useEffect(() => {
        if (categories.length > 0) {
            const newTabContent = [
                <ExpiryReasonTab
                    setBusy={setBusy}
                    categories={categories}
                    setCategories={setCategories}
                    openModal={setShowModal}
                    onFilterChange={onFilterChange} />
            ];
            setTabContent(newTabContent);
        }
    }, [categories]);

    const buildCategories = async () => {
        try{
            setBusy(true);
            const expiryReasons = await getExpiryReasons();
            setCategories([{
                displayName: translate.byKey(TranslationKeys.operationalCompliance),
                heading: translate.byKey(TranslationKeys.operationalCompliance),
                value: "",
                id: 0,
                count: expiryReasons.pagination.totalCount
            }]);
            setBusy(false);
        } catch (error) {
            showError(error, toast.warn);
        }
    }

    const onFilterChange = (filters: Filter[]) => {
        const siteIds = getListOfSiteIdsFromFilter(filters);
        updateTabCount(siteIds)
    };

    const saveItem = async (values: ExpiryReasonModel) => {
        const expiryReason: ExpiryReasonModel = {
            id: values.id ?? 0,
            name: values.name,
            nameOriginal: values.nameOriginal,
            duration: values.duration,
            durationValue: values.durationValue,
            durationWarning: values.durationWarning,
            durationUnit: values.durationUnit,
            durationWarnValue: values.durationWarnValue,
            eventExpiry: values.eventExpiry,
            eventWarning: values.eventWarning,
        }

        const createNew = values.id ? false : true;

        setBusy(true);
        saveExpiryReason(expiryReason, createNew).then(() => {
            toast.success(translate.byKey(TranslationKeys.success));
            if (currentItem.id === 0) {
                setCategories([{ ...categories[0], count: (categories[0]?.count ?? 1) + 1 }]);
            }
            handleClose();
        })
        .catch(error => {
            dialog.pushError({ title: translate.byKey(TranslationKeys.error), message: getErrorMessage(error) });
        })
        .finally(() => {
            setBusy(false);
        });
    };

    const handleClose = () => {
        setShowModal(false);
        setCurrentItem(initialValues);
    }

    return  <ActivityFrame busy={busy} className="flex flex-grow overflow-y-hidden">
        <div id="container-tabs" className="pt-4 pl-4 pb-4 flex flex-col flex-grow ">
            <Breadcrumb
                path={[
                    { value: '/access-control/configuration', displayName: translate.byKey(TranslationKeys.configuration) },
                    { value: '/access-control/configuration/operational-compliance', displayName: translate.byKey(TranslationKeys.operationalCompliance) }]}
                onClick={(item) => history.push(item.value)}>
            </Breadcrumb>
            <Tabs
                className="overflow-y-hidden"
                tabs={getTabsListFromLabels(categories)}
                onSelect={(i) => setSelected(i)}
                setSelectedTab={setSelected}
                selected={selected}
                onAdd={() => { }}
                add={false}
                tabStyle={{}}>
                {tabContent[selected]}
                <div className="flex flex-row justify-end button-cluster">
                    <Restricted to={`add.${routeName}.element`}>
                        <button className="flex rounded-lg bg-lime text-white p-2 shadow-lg" onClick={() => setShowModal(true)}>
                            <img src={plusIcon} className="px-2 w-8 py-1" alt="" />
                            <span className="mr-2">{translate.byKey(TranslationKeys.add)}</span>
                        </button>
                    </Restricted>
                </div>
            </Tabs>
        </div>
        <ExpiryReasonModal currentItem={currentItem} isOpen={showModal} onClose={handleClose} saveItem={saveItem} />
    </ActivityFrame>
};

export default ExpiryReasons;
